import { PreferenceStore } from './PreferenceStore';
import { EventMonitor } from './EventMonitor';
import { AudioDelivery, electAudioTab } from './AudioDelivery';
import { AudioPlayer } from './AudioPlayer';
import { accountSoundPreferences } from './AccountSoundPreferences';
import { getDispatcherEvents } from './DispatcherEvents';

// One authenticated application lifetime, independent of router/map visibility.
export class SoundAlertsRuntime {
  constructor({ accountId, current, notify, service = accountSoundPreferences,
    request = getDispatcherEvents, playerFactory = (options) => new AudioPlayer(options),
    elect = electAudioTab, now = Date.now, host = window }) {
    Object.assign(this, { accountId, current, notify, request, elect, now, host });
    this.active = true;
    this.paused = false;
    this.leader = false;
    this.previewing = false;
    this.preferenceDue = 0;
    this.preferenceFailures = 0;
    this.extra = { activated: false, playbackStatus: 'inactive', monitoringStatus: 'inactive' };
    this.player = playerFactory({ notify: (playbackStatus) => {
      this.extra.playbackStatus = playbackStatus;
      if (playbackStatus === 'blocked') this.extra.activated = false;
      this.emit();
    } });
    this.store = new PreferenceStore({
      service, current: () => this.valid(), notify: () => this.emit(),
      silence: () => this.silence(),
      scopeChanged: () => this.stopLeader(),
      forbidden: () => this.forbid(),
    });
    this.pagehide = () => {
      this.paused = true;
      this.stopLeader();
      this.player.stop();
    };
    this.pageshow = () => {
      this.paused = false;
      this.preferenceDue = 0;
      this.refresh();
    };
    this.channel = null;
    try {
      if (host.BroadcastChannel) {
        this.channel = new host.BroadcastChannel(`dop:sound-settings:${accountId}`);
        this.channel.onmessage = ({ data }) => {
          if (!this.valid() || data?.epoch !== this.store.epoch || data?.kind !== 'refresh') return;
          // Signals trigger a server read; no preferences are trusted from another tab.
          this.silence();
          this.store.healthy = false;
          this.stopLeader();
          this.preferenceDue = 0;
          this.refresh();
        };
      }
    } catch (_) { /* periodic server revision checking remains available */ }
  }
  valid() { return this.active && this.current(); }
  emit() {
    if (!this.valid() || !this.store) return;
    this.notify({ ...this.store.state, ...this.extra,
      canSave: this.store.healthy && !this.store.loading && this.store.state.status === 'ready' });
  }
  start() {
    this.host.addEventListener('pagehide', this.pagehide);
    this.host.addEventListener('pageshow', this.pageshow);
    this.timer = this.host.setInterval(() => this.tick(), 250);
    this.refresh();
  }
  async refresh() {
    if (!this.valid() || this.paused || this.refreshing) return;
    this.refreshing = true;
    try {
      const ok = await this.store.load();
      if (!this.valid()) return;
      this.preferenceFailures = ok ? 0 : Math.min(this.preferenceFailures + 1, 3);
      this.preferenceDue = this.now() + Math.min(30000, 5000 * (2 ** this.preferenceFailures));
      this.syncLeadership();
      this.emit();
    } finally { this.refreshing = false; }
  }
  tick() {
    if (!this.valid()) { this.dispose(); return; }
    if (this.paused) return;
    this.syncLeadership();
    if (this.monitor) this.monitor.tick();
    // Followers/unactivated tabs revalidate permissions, scope and preferences;
    // only the activated leader fetches the operational event feed.
    if (!this.leader && this.now() >= this.preferenceDue) this.refresh();
  }
  syncLeadership() {
    const eligible = this.valid() && !this.paused && this.player.activated &&
      this.extra.activated && this.store.healthy && this.store.epoch;
    if (!eligible) { this.stopLeader(); return; }
    if (this.releaseLeader) return;
    const scope = this.store.epoch;
    this.extra.monitoringStatus = 'standby';
    this.releaseLeader = this.elect({
      accountId: this.accountId,
      status: (status) => { this.extra.monitoringStatus = status; this.emit(); },
      start: () => {
        if (!this.valid() || this.paused || scope !== this.store.epoch) return undefined;
        this.leader = true;
        this.extra.monitoringStatus = 'starting';
        const current = () => this.valid() && !this.paused && this.leader &&
          scope === this.store.epoch && this.store.healthy && this.extra.activated;
        const delivery = new AudioDelivery({
          scope: `${this.accountId}:${scope}`, player: this.player, current, now: this.now,
          preferences: () => {
            const value = this.store.effective();
            return this.previewing ? { ...value, masterEnabled: false } : value;
          },
          status: (status) => { this.extra.monitoringStatus = status; this.emit(); },
        });
        const monitor = new EventMonitor({
          request: this.request, scope, current, now: this.now,
          deliver: (event) => delivery.accept(event), silence: () => delivery.clear(),
          context: async (page) => {
            if (page.scope_epoch !== this.store.epoch || page.preferences_revision !== this.store.revision) {
              this.silence();
              await this.refresh();
              return false; // Changed preferences/scope: consume no backlog sounds.
            }
            return true;
          },
          status: (status) => {
            if (status === 'forbidden') { this.forbid(); return; }
            this.extra.monitoringStatus = status;
            this.emit();
          },
        });
        this.delivery = delivery;
        this.monitor = monitor;
        this.emit();
        monitor.tick();
        return () => {
          monitor.dispose();
          delivery.dispose();
          if (this.monitor === monitor) {
            this.monitor = null;
            this.delivery = null;
          }
          this.leader = false;
        };
      },
    });
    this.emit();
  }
  stopLeader() {
    // Silence synchronously, before returning the lock to a waiting tab.
    if (this.monitor) this.monitor.dispose();
    if (this.delivery) this.delivery.dispose();
    this.monitor = null;
    this.delivery = null;
    if (this.releaseLeader) this.releaseLeader();
    this.releaseLeader = null;
    this.leader = false;
  }
  silence() {
    if (this.delivery) this.delivery.clear();
    else this.player.stop();
  }
  update(patch) { this.store.update(patch); }
  async save() {
    await this.store.save();
    if (!this.valid()) return;
    // Clear all outstanding work and rebaseline even if settings were enabled.
    if (this.monitor) this.monitor.reset();
    this.syncLeadership();
    if (this.channel) this.channel.postMessage({ kind: 'refresh', epoch: this.store.epoch });
    this.emit();
  }
  async activate() {
    if (!this.valid() || !this.store.healthy) return;
    const ok = await this.player.activate();
    if (!this.valid()) return;
    this.extra.activated = Boolean(ok);
    // No pending events accumulated before activation.
    this.syncLeadership();
    this.emit();
  }
  async preview(chime) {
    if (!this.valid() || !this.store.saved) return;
    this.previewing = true;
    this.silence();
    await this.player.preview(chime, this.store.state.preferences.volume);
    this.previewing = false;
    if (!this.valid()) return;
    this.extra.activated = this.player.activated;
    this.syncLeadership();
    this.emit();
  }
  forbid() {
    this.stopLeader();
    this.store.dispose();
    this.player.dispose();
    this.extra = { activated: false, playbackStatus: 'inactive', monitoringStatus: 'forbidden' };
    // Render the cleared error state once; no retries before authentication changes.
    this.emit();
    this.dispose();
  }
  dispose() {
    if (!this.active) return;
    this.active = false;
    this.host.clearInterval(this.timer);
    this.host.removeEventListener('pagehide', this.pagehide);
    this.host.removeEventListener('pageshow', this.pageshow);
    this.stopLeader();
    this.store.dispose();
    this.player.dispose();
    if (this.channel) this.channel.close();
  }
}
